import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/ui/EmptyState';
import { outreachApi } from '../api/outreachApi';
import BucketChart from '../components/BucketChart';
import InfoHint from '../components/InfoHint';
import { mailboxColor } from '../mailboxColors';
import '../outreach.css';

const RANGES = [7, 30];

const EMPTY_SERVER_FIELDS = {
  imapHost: '', imapPort: 993, imapSecurity: 'ssl',
  smtpHost: '', smtpPort: 587, smtpSecurity: 'starttls',
};

const EMPTY_FORM = {
  email: '', displayName: '', username: '', password: '',
  ...EMPTY_SERVER_FIELDS,
};

// SMTP defaults to 587/STARTTLS everywhere - outbound 465 is blocked from
// this host for every provider tested (Gmail, Zoho, Office365 alike).
const PROVIDER_PRESETS = [
  { key: 'google', label: 'Google', imapHost: 'imap.gmail.com', imapPort: 993, imapSecurity: 'ssl', smtpHost: 'smtp.gmail.com', smtpPort: 587, smtpSecurity: 'starttls' },
  { key: 'microsoft', label: 'Microsoft 365', imapHost: 'outlook.office365.com', imapPort: 993, imapSecurity: 'ssl', smtpHost: 'smtp.office365.com', smtpPort: 587, smtpSecurity: 'starttls' },
  { key: 'zoho', label: 'Zoho', imapHost: 'imappro.zoho.eu', imapPort: 993, imapSecurity: 'ssl', smtpHost: 'smtppro.zoho.eu', smtpPort: 587, smtpSecurity: 'starttls' },
];

const WARMUP_MESSAGE = {
  not_configured: 'notConfigured',
  subscription_inactive: 'subscriptionInactive',
  unreachable: 'unreachable',
};

const WARMUP_BADGE = {
  active: 'is-mailbox',
  paused: 'is-ooo',
  unknown: 'is-ooo',
  not_configured: '',
};

// One line and a bar; the four lines of raw counters this used to print are
// still here, one click away, instead of being the default reading.
function WarmupCell({ overview, accountId, t }) {
  if (!overview || overview.availability !== 'ok') return <span className="lead-history-source">-</span>;
  const entry = overview.mailboxes.find((m) => m.account_id === accountId);
  if (!entry) return <span className="lead-history-source">-</span>;

  const badge = <span className={`unibox-badge ${WARMUP_BADGE[entry.state]}`}>{t(`outreach.warmup.state.${entry.state}`)}</span>;
  if (entry.state === 'not_configured') return <div className="warmup-cell">{badge}</div>;

  const done = entry.sent_today + entry.received_today;
  const progress = entry.scheduled_today ? Math.min(done / entry.scheduled_today, 1) : 0;
  const spamming = entry.spam_rate_last_14_days > 0;

  return (
    <div className="warmup-cell">
      <div className="warmup-summary">
        {badge}
        <span className="lead-history-source">
          {t('outreach.warmup.todayShort', { sent: done, scheduled: entry.scheduled_today })}
        </span>
        {spamming && (
          <span className="unibox-badge is-bounce">
            {t('outreach.warmup.spamRate', { rate: entry.spam_rate_last_14_days })}
          </span>
        )}
      </div>
      <div className="meter">
        <div
          className={`meter-fill${spamming ? ' is-danger' : ''}`}
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
      <details className="warmup-details">
        <summary className="lead-history-source">{t('outreach.warmup.details')}</summary>
        <div className="warmup-detail-lines">
          {entry.next_email_after && (
            <span className="lead-history-source">
              {t('outreach.warmup.next', {
                when: new Date(entry.next_email_after).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              })}
            </span>
          )}
          <span className="lead-history-source">
            {t('outreach.warmup.target', { target: entry.target_per_day, rate: entry.reply_rate_percent })}
            {entry.ramp_up ? ` · ${t('outreach.warmup.rampUp', { value: entry.ramp_up })}` : ''}
          </span>
          <span className="lead-history-source">
            {t('outreach.warmup.windows', { sent: entry.sent_last_45_days, spam: entry.spam_last_14_days })}
          </span>
          {entry.spam_rate_last_14_days === null && (
            <span className="lead-history-source">{t('outreach.warmup.spamRateUnknown')}</span>
          )}
          {entry.dashboard_url && (
            <a className="warmup-link" href={entry.dashboard_url} target="_blank" rel="noreferrer">
              {t('outreach.warmup.dashboard')}
            </a>
          )}
        </div>
      </details>
    </div>
  );
}

// The mailbox's own ceiling. A campaign paces inside it and can never raise
// it, so this is the knob that actually protects a provider account.
function MailboxLimits({ account, onSaved, t }) {
  // Stored in seconds, shown in minutes - the labels say minutes and 540 in
  // that box would read as nine hours.
  const [values, setValues] = useState({
    dailyCap: account.limits.daily_cap,
    minGapMinutes: account.limits.min_gap_seconds / 60,
    jitterMinutes: account.limits.jitter_seconds / 60,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await outreachApi.updateAccountLimits(account.id, {
        dailyCap: values.dailyCap,
        minGapSeconds: Math.round(values.minGapMinutes * 60),
        jitterSeconds: Math.round(values.jitterMinutes * 60),
      });
      setSaved(true);
      await onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const field = (key, labelKey, hintKey, min) => (
    <label className="campaign-inline">
      {t(labelKey)}
      <InfoHint text={t(hintKey)} />
      <input
        className="unibox-select campaign-number"
        type="number"
        min={min}
        value={values[key]}
        onChange={(e) => {
          setSaved(false);
          setValues({ ...values, [key]: Number(e.target.value) });
        }}
      />
    </label>
  );

  return (
    <div className="mailbox-details-block">
      <strong>{t('outreach.limits.title')}</strong>
      <p className="unibox-reply-hint">{t('outreach.limits.explainer')}</p>
      <div className="mailbox-limits-fields">
        {field('dailyCap', 'outreach.limits.dailyCap', 'outreach.limits.dailyCapHint', 1)}
        {field('minGapMinutes', 'outreach.limits.minGap', 'outreach.limits.minGapHint', 1)}
        {field('jitterMinutes', 'outreach.limits.jitter', 'outreach.limits.jitterHint', 0)}
        <button className="unibox-button" onClick={save} disabled={busy}>
          {busy ? t('outreach.limits.saving') : t('outreach.limits.save')}
        </button>
        {saved && <span className="lead-history-source">{t('outreach.limits.saved')}</span>}
        {error && <span className="unibox-error">{String(error)}</span>}
      </div>
    </div>
  );
}

// Present / missing / unknown, each carrying the record that fixes it - a red
// "missing" with no next step is just an alarm.
function DnsBadges({ dns, t }) {
  const checks = [
    { key: 'SPF', found: dns?.spf_found, hint: t('outreach.dns.fixSpf') },
    { key: 'DKIM', found: dns?.dkim_found, hint: t('outreach.dns.fixDkim') },
    { key: 'DMARC', found: dns?.dmarc_found, hint: t('outreach.dns.fixDmarc') },
  ];
  return (
    <div className="dns-badges">
      {checks.map(({ key, found, hint }) => (
        <span
          key={key}
          className={`badge ${found ? 'badge-success' : found === null ? 'badge-neutral' : 'badge-danger'}`}
        >
          {key}
          {found ? ' ✓' : found === null ? ' ?' : ''}
          {!found && <InfoHint text={found === null ? t('outreach.dns.unknown') : hint} />}
        </span>
      ))}
    </div>
  );
}

function DailySends({ accounts, t }) {
  const { i18n } = useTranslation('common');
  const [days, setDays] = useState(RANGES[0]);
  const [series, setSeries] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let current = true;
    outreachApi
      .dailyAnalytics(days)
      .then((data) => current && setSeries(data))
      .catch((e) => current && setError(e?.response?.data?.detail || e.message));
    return () => {
      current = false;
    };
  }, [days]);

  if (error) return <div className="unibox-banner">{String(error)}</div>;
  if (!series) return <p>{t('outreach.loading')}</p>;

  const totals = series.days.reduce(
    (sum, day) => ({
      sent: sum.sent + day.sent,
      replies: sum.replies + day.replies,
      bounced: sum.bounced + day.bounced_hard,
      unsubscribed: sum.unsubscribed + day.unsubscribed,
    }),
    { sent: 0, replies: 0, bounced: 0, unsubscribed: 0 }
  );

  const perMailbox = accounts
    .map((account) => ({
      account,
      sent: series.days.reduce(
        (sum, day) => sum + (day.by_mailbox.find((m) => m.account_id === account.id)?.sent || 0),
        0
      ),
    }))
    .filter((row) => row.sent > 0);

  // Mail from a since-disconnected mailbox still happened, so it keeps a
  // segment - otherwise the bars would not add up to the total below them.
  const buckets = series.days.map((day) => {
    const segments = accounts.map((account) => ({
      key: account.id,
      value: day.by_mailbox.find((m) => m.account_id === account.id)?.sent || 0,
      color: mailboxColor(accounts, account.id),
    }));
    const known = segments.reduce((sum, segment) => sum + segment.value, 0);
    if (day.sent > known) {
      segments.push({ key: 'gone', value: day.sent - known, color: 'var(--border-strong)' });
    }
    return {
      key: day.date,
      label: Number(day.date.slice(8, 10)),
      title: `${day.date} - ${t('outreach.daily.tooltip', {
        sent: day.sent,
        replies: day.replies,
        bounced: day.bounced_hard,
      })}`,
      segments,
    };
  });

  const fromGoneMailboxes = totals.sent - perMailbox.reduce((sum, row) => sum + row.sent, 0);

  return (
    <section className="daily-sends">
      <div className="daily-sends-head">
        <h2 className="daily-sends-title">
          {t('outreach.daily.title')}
          <InfoHint text={t('outreach.daily.hint', { timezone: series.timezone })} />
        </h2>
        <div className="daily-sends-ranges">
          {RANGES.map((range) => (
            <button
              key={range}
              type="button"
              className={`unibox-page-button${range === days ? ' is-active' : ''}`}
              onClick={() => setDays(range)}
            >
              {t('outreach.daily.lastDays', { days: range })}
            </button>
          ))}
        </div>
      </div>

      <p className="daily-sends-range">
        {[series.days[0], series.days[series.days.length - 1]]
          .map((day) =>
            new Date(`${day.date}T12:00:00`).toLocaleDateString(i18n.language, {
              day: 'numeric',
              month: 'short',
            })
          )
          .join(' - ')}
      </p>

      <BucketChart buckets={buckets} emptyLabel={t('outreach.daily.empty')} labelEvery={days > 14 ? 3 : 1} />

      <div className="daily-sends-totals">
        <span>{t('outreach.daily.sent', { n: totals.sent })}</span>
        <span>{t('outreach.daily.replies', { n: totals.replies })}</span>
        <span>{t('outreach.daily.bounced', { n: totals.bounced })}</span>
        <span>{t('outreach.daily.unsubscribed', { n: totals.unsubscribed })}</span>
      </div>

      {totals.sent > 0 && (
        <div className="daily-sends-legend">
          {perMailbox.map(({ account, sent }) => (
            <span key={account.id} className="daily-sends-legend-item">
              <span
                className="unibox-mailbox-dot"
                style={{ background: mailboxColor(accounts, account.id) }}
              />
              {account.email} - {sent}
            </span>
          ))}
          {fromGoneMailboxes > 0 && (
            <span className="daily-sends-legend-item">
              <span className="unibox-mailbox-dot" style={{ background: 'var(--border-strong)' }} />
              {t('outreach.daily.disconnected', { n: fromGoneMailboxes })}
            </span>
          )}
        </div>
      )}
    </section>
  );
}

export default function MailboxesPage() {
  const { t } = useTranslation('common');
  const { token } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [reports, setReports] = useState({});
  const [warmup, setWarmup] = useState(null);

  const runDeliverabilityCheck = async (account) => {
    try {
      const report = await outreachApi.deliverabilityCheck(account.id);
      setReports((current) => ({ ...current, [account.id]: report }));
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e.message);
    }
  };

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setAccounts(await outreachApi.listAccounts());
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWarmup = useCallback(async () => {
    try {
      setWarmup(await outreachApi.warmupOverview());
    } catch (e) {
      // 403 is the feature gate, not a failure: the section stays hidden.
      if (e?.response?.status !== 403) setWarmup({ availability: 'unreachable', mailboxes: [] });
    }
  }, []);

  useEffect(() => {
    loadAccounts();
    loadWarmup();
  }, [token, loadAccounts, loadWarmup]);

  const handleConnect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    setConnectError(null);
    try {
      await outreachApi.connectAccount({
        ...form,
        imapPort: Number(form.imapPort),
        smtpPort: Number(form.smtpPort),
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadAccounts();
    } catch (e) {
      setConnectError(e?.response?.data?.detail || e.message);
    } finally {
      setConnecting(false);
    }
  };

  // No overview at all means no warm-up here: hide the column, don't empty it.
  const showWarmup = warmup !== null;

  const handleDisconnect = async (account) => {
    if (!window.confirm(t('outreach.disconnectConfirm', { email: account.email }))) return;
    await outreachApi.disconnectAccount(account.id);
    await loadAccounts();
  };

  return (
    <div className="mailboxes-page">
      <h1 className="unibox-title">{t('outreach.mailboxes')}</h1>
      <p style={{ color: 'var(--text-muted)' }}>
        <Link to="/outreach">← {t('nav.outreach', 'Conversations')}</Link>
      </p>

      {loadError && <div className="unibox-banner">{String(loadError)}</div>}

      {warmup?.pool && (
        <p className="warmup-pool">
          {t('outreach.warmup.pool', {
            available: warmup.pool.available_per_day,
            total: warmup.pool.total_per_day,
            reserved: warmup.pool.reserved_per_day,
            mailboxes: warmup.pool.mailboxes,
          })}
          {warmup.pool.needing_attention > 0 &&
            ` ${t('outreach.warmup.poolAttention', { n: warmup.pool.needing_attention })}`}
        </p>
      )}
      {warmup && warmup.availability !== 'ok' && (
        <div className="unibox-banner">{t(`outreach.warmup.${WARMUP_MESSAGE[warmup.availability]}`)}</div>
      )}

      {loading ? (
        <p>{t('outreach.loading')}</p>
      ) : accounts.length === 0 ? (
        <EmptyState title={t('outreach.emptyNoMailbox')} text={t('outreach.emptyNoMailboxLink')}>
          <button className="unibox-button" onClick={() => setShowForm(true)}>
            <Plus size={14} /> {t('outreach.connectMailbox')}
          </button>
        </EmptyState>
      ) : (
        <div className="mailboxes-scroll">
        <table className="admin-table" style={{ width: '100%', marginBottom: 'var(--space-6)' }}>
          <thead>
            <tr>
              <th>{t('outreach.email')}</th>
              <th>{t('outreach.status')}</th>
              <th>{t('outreach.warmup.reputation')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <React.Fragment key={account.id}>
              <tr>
                <td>{account.email}</td>
                <td>
                  <span className={`badge ${account.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                    {account.status}
                  </span>
                </td>
                <td>
                  <DnsBadges dns={account.health?.dns} t={t} />
                </td>
                <td>
                  <button className="unibox-page-button" onClick={() => runDeliverabilityCheck(account)}>
                    {t('outreach.check.short')}
                  </button>{' '}
                  <button className="unibox-page-button" onClick={() => handleDisconnect(account)}>
                    {t('outreach.disconnect')}
                  </button>
                </td>
              </tr>
              <tr className="mailbox-expand-row">
                <td colSpan={4}>
                  <details className="mailbox-details">
                    <summary>{t('outreach.mailboxDetails')}</summary>
                    <div className="mailbox-details-body">
                      <MailboxLimits account={account} onSaved={loadAccounts} t={t} />
                      {showWarmup && (
                        <div className="mailbox-details-block">
                          <strong>{t('outreach.warmup.title')}</strong>
                          <WarmupCell overview={warmup} accountId={account.id} t={t} />
                        </div>
                      )}
                    </div>
                  </details>
                  {reports[account.id] && (
                    <div className="dns-report">
                      <DnsBadges dns={reports[account.id].dns} t={t} />
                      <span className="unibox-badge">
                        {t('outreach.check.blocklists', {
                          checked: reports[account.id].blocklists.checked.length,
                          hits: reports[account.id].blocklists.hits.length,
                        })}
                      </span>
                      {reports[account.id].blocklists.unreachable.length > 0 && (
                        <span className="unibox-error">
                          {t('outreach.check.blocklistsUnreachable', {
                            names: reports[account.id].blocklists.unreachable.join(', '),
                          })}
                        </span>
                      )}
                      {reports[account.id].blocklists.hits.map((hit) => (
                        <span key={hit.list_name} className="unibox-error">
                          {hit.list_name}: {hit.target}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {!loading && accounts.length > 0 && <DailySends accounts={accounts} t={t} />}

      <button className="unibox-button" onClick={() => setShowForm(true)}>
        <Plus size={14} /> {t('outreach.connectMailbox')}
      </button>

      {showForm && (
        <div className="mailboxes-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="mailboxes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mailboxes-modal-head">
              <h2 className="unibox-detail-subject">{t('outreach.connectMailbox')}</h2>
              <button className="unibox-page-button" onClick={() => setShowForm(false)}>
                <X size={14} />
              </button>
            </div>

            <div className="provider-presets">
              <span className="lead-history-source">{t('outreach.quickFill')}</span>
              {PROVIDER_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className="unibox-page-button"
                  onClick={() => setForm((current) => ({ ...current, ...preset }))}
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                className="unibox-page-button"
                onClick={() => setForm((current) => ({ ...current, ...EMPTY_SERVER_FIELDS }))}
              >
                {t('outreach.providerCustom')}
              </button>
            </div>

            <form onSubmit={handleConnect} className="mailboxes-form">
              <input className="unibox-select" placeholder={t('outreach.email')} value={form.email} required
                onChange={(e) => setForm({ ...form, email: e.target.value, username: e.target.value })} />
              <input className="unibox-select" placeholder={t('outreach.displayName')} value={form.displayName} required
                onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
              <input className="unibox-select" placeholder={t('outreach.password')} type="password" value={form.password} required
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <div className="mailboxes-form-row">
                <input className="unibox-select" placeholder={t('outreach.imapHost')} value={form.imapHost} required
                  onChange={(e) => setForm({ ...form, imapHost: e.target.value })} />
                <input className="unibox-select mailboxes-port" placeholder={t('outreach.imapPort')} type="number" value={form.imapPort} required
                  onChange={(e) => setForm({ ...form, imapPort: e.target.value })} />
                <select className="unibox-select" value={form.imapSecurity}
                  onChange={(e) => setForm({ ...form, imapSecurity: e.target.value })}>
                  <option value="ssl">{t('outreach.security.ssl')}</option>
                  <option value="starttls">{t('outreach.security.starttls')}</option>
                  <option value="none">{t('outreach.security.none')}</option>
                </select>
              </div>
              <div className="mailboxes-form-row">
                <input className="unibox-select" placeholder={t('outreach.smtpHost')} value={form.smtpHost} required
                  onChange={(e) => setForm({ ...form, smtpHost: e.target.value })} />
                <input className="unibox-select mailboxes-port" placeholder={t('outreach.smtpPort')} type="number" value={form.smtpPort} required
                  onChange={(e) => setForm({ ...form, smtpPort: e.target.value })} />
                <select className="unibox-select" value={form.smtpSecurity}
                  onChange={(e) => setForm({ ...form, smtpSecurity: e.target.value })}>
                  <option value="ssl">{t('outreach.security.ssl')}</option>
                  <option value="starttls">{t('outreach.security.starttls')}</option>
                  <option value="none">{t('outreach.security.none')}</option>
                </select>
              </div>
              <button className="unibox-button" type="submit" disabled={connecting}>
                {connecting ? t('outreach.testingConnection') : t('outreach.connect')}
              </button>
              {connectError && <div className="unibox-error">{String(connectError)}</div>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
